/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { AdminBundesbankSettlementInstructionStatusEnum } from './AdminBundesbankSettlementInstructionStatusEnum';
import {
    AdminBundesbankSettlementInstructionStatusEnumFromJSON,
    AdminBundesbankSettlementInstructionStatusEnumFromJSONTyped,
    AdminBundesbankSettlementInstructionStatusEnumToJSON,
} from './AdminBundesbankSettlementInstructionStatusEnum';
import type { Money } from './Money';
import {
    MoneyFromJSON,
    MoneyFromJSONTyped,
    MoneyToJSON,
} from './Money';
import type { Wallet } from './Wallet';
import {
    WalletFromJSON,
    WalletFromJSONTyped,
    WalletToJSON,
} from './Wallet';
import type { AdminBundesbankPaymentInstruction } from './AdminBundesbankPaymentInstruction';
import {
    AdminBundesbankPaymentInstructionFromJSON,
    AdminBundesbankPaymentInstructionFromJSONTyped,
    AdminBundesbankPaymentInstructionToJSON,
} from './AdminBundesbankPaymentInstruction';
import type { AdminSettlementNestedInvestor } from './AdminSettlementNestedInvestor';
import {
    AdminSettlementNestedInvestorFromJSON,
    AdminSettlementNestedInvestorFromJSONTyped,
    AdminSettlementNestedInvestorToJSON,
} from './AdminSettlementNestedInvestor';

/**
 * 
 * @export
 * @interface AdminBundesbankSettlementInstruction
 */
export interface AdminBundesbankSettlementInstruction {
    /**
     * 
     * @type {string}
     * @memberof AdminBundesbankSettlementInstruction
     */
    readonly id: string;
    /**
     * 
     * @type {string}
     * @memberof AdminBundesbankSettlementInstruction
     */
    readonly productId: string;
    /**
     * 
     * @type {string}
     * @memberof AdminBundesbankSettlementInstruction
     */
    readonly productName: string;
    /**
     * 
     * @type {string}
     * @memberof AdminBundesbankSettlementInstruction
     */
    correlationId: string;
    /**
     * 
     * @type {Wallet}
     * @memberof AdminBundesbankSettlementInstruction
     */
    readonly sourceWallet: Wallet;
    /**
     * 
     * @type {AdminSettlementNestedInvestor}
     * @memberof AdminBundesbankSettlementInstruction
     */
    readonly sourceInvestor: AdminSettlementNestedInvestor;
    /**
     * 
     * @type {Wallet}
     * @memberof AdminBundesbankSettlementInstruction
     */
    readonly targetWallet: Wallet;
    /**
     * 
     * @type {AdminSettlementNestedInvestor}
     * @memberof AdminBundesbankSettlementInstruction
     */
    readonly targetInvestor: AdminSettlementNestedInvestor;
    /**
     * 
     * @type {number}
     * @memberof AdminBundesbankSettlementInstruction
     */
    productAmount: number;
    /**
     * 
     * @type {Money}
     * @memberof AdminBundesbankSettlementInstruction
     */
    paymentAmount: Money;
    /**
     * 
     * @type {number}
     * @memberof AdminBundesbankSettlementInstruction
     */
    timeoutDelay: number;
    /**
     * 
     * @type {AdminBundesbankSettlementInstructionStatusEnum}
     * @memberof AdminBundesbankSettlementInstruction
     */
    readonly status: AdminBundesbankSettlementInstructionStatusEnum;
    /**
     * 
     * @type {AdminBundesbankPaymentInstruction}
     * @memberof AdminBundesbankSettlementInstruction
     */
    readonly paymentInstruction: AdminBundesbankPaymentInstruction;
    /**
     * 
     * @type {Date}
     * @memberof AdminBundesbankSettlementInstruction
     */
    readonly calculatedTimeout: Date | null;
    /**
     * 
     * @type {string}
     * @memberof AdminBundesbankSettlementInstruction
     */
    readonly htlcHash: string;
    /**
     * 
     * @type {string}
     * @memberof AdminBundesbankSettlementInstruction
     */
    readonly htlcAddress: string;
    /**
     * 
     * @type {Date}
     * @memberof AdminBundesbankSettlementInstruction
     */
    readonly createdAt: Date;
}



/**
 * Check if a given object implements the AdminBundesbankSettlementInstruction interface.
 */
export function instanceOfAdminBundesbankSettlementInstruction(value: object): value is AdminBundesbankSettlementInstruction {
    if (!('id' in value) || value['id'] === undefined) return false;
    if (!('productId' in value) || value['productId'] === undefined) return false;
    if (!('productName' in value) || value['productName'] === undefined) return false;
    if (!('correlationId' in value) || value['correlationId'] === undefined) return false;
    if (!('sourceWallet' in value) || value['sourceWallet'] === undefined) return false;
    if (!('sourceInvestor' in value) || value['sourceInvestor'] === undefined) return false;
    if (!('targetWallet' in value) || value['targetWallet'] === undefined) return false;
    if (!('targetInvestor' in value) || value['targetInvestor'] === undefined) return false;
    if (!('productAmount' in value) || value['productAmount'] === undefined) return false;
    if (!('paymentAmount' in value) || value['paymentAmount'] === undefined) return false;
    if (!('timeoutDelay' in value) || value['timeoutDelay'] === undefined) return false;
    if (!('status' in value) || value['status'] === undefined) return false;
    if (!('paymentInstruction' in value) || value['paymentInstruction'] === undefined) return false;
    if (!('calculatedTimeout' in value) || value['calculatedTimeout'] === undefined) return false;
    if (!('htlcHash' in value) || value['htlcHash'] === undefined) return false;
    if (!('htlcAddress' in value) || value['htlcAddress'] === undefined) return false;
    if (!('createdAt' in value) || value['createdAt'] === undefined) return false;
    return true;
}

export function AdminBundesbankSettlementInstructionFromJSON(json: any): AdminBundesbankSettlementInstruction {
    return AdminBundesbankSettlementInstructionFromJSONTyped(json, false);
}

export function AdminBundesbankSettlementInstructionFromJSONTyped(json: any, ignoreDiscriminator: boolean): AdminBundesbankSettlementInstruction {
    if (json == null) {
        return json;
    }
    return {
        
        'id': json['id'],
        'productId': json['product_id'],
        'productName': json['product_name'],
        'correlationId': json['correlation_id'],
        'sourceWallet': WalletFromJSON(json['source_wallet']),
        'sourceInvestor': AdminSettlementNestedInvestorFromJSON(json['source_investor']),
        'targetWallet': WalletFromJSON(json['target_wallet']),
        'targetInvestor': AdminSettlementNestedInvestorFromJSON(json['target_investor']),
        'productAmount': json['product_amount'],
        'paymentAmount': MoneyFromJSON(json['payment_amount']),
        'timeoutDelay': json['timeout_delay'],
        'status': AdminBundesbankSettlementInstructionStatusEnumFromJSON(json['status']),
        'paymentInstruction': AdminBundesbankPaymentInstructionFromJSON(json['payment_instruction']),
        'calculatedTimeout': (json['calculated_timeout'] == null ? null : new Date(json['calculated_timeout'])),
        'htlcHash': json['htlc_hash'],
        'htlcAddress': json['htlc_address'],
        'createdAt': (new Date(json['created_at'])),
    };
}

export function AdminBundesbankSettlementInstructionToJSON(value?: Omit<AdminBundesbankSettlementInstruction, 'id'|'product_id'|'product_name'|'source_wallet'|'source_investor'|'target_wallet'|'target_investor'|'status'|'payment_instruction'|'calculated_timeout'|'htlc_hash'|'htlc_address'|'created_at'> | null): any {
    if (value == null) {
        return value;
    }
    return {
        
        'correlation_id': value['correlationId'],
        'product_amount': value['productAmount'],
        'payment_amount': MoneyToJSON(value['paymentAmount']),
        'timeout_delay': value['timeoutDelay'],
    };
}

